import React, { useEffect, useState } from "react";
import FeatureCard from "./FeatureCard";
import "../../_css/AIFeedback.css";
import { formatDateLong, formatGrade } from "../helper/formatters";
import { useDispatch } from "react-redux";
import { downloadExcel, getAllPdfs, getClassReport } from "../../requests";
import { notifyError } from "../helper/Toaster";
import { deleteAuthToken } from "../../utils/helper";
import { useNavigate, useLocation } from "react-router-dom";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { LockKeyhole } from "lucide-react";

import CustomChart from "../common/CustomChart";
import SearchableDropdown from "../common/SearchableDropdown";

const ClassUI = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState(null);
  const [filteredstudents, setFilteredStudents] = useState([]);
  const [selectedTog, setselectedTog] = useState("top");
  const [name, setName] = useState("");
  const [myClass, setMyClass] = useState(1);
  const [subjectWiseAvg, setSubjectWiseAvg] = useState([]);
  const [comparisonWithOtherClasses, setComparisonWithOtherClasses] = useState(
    []
  );
  const [totalStudents, setTotalStudents] = useState(0);
  const [subject, setSubject] = useState("All");

  const [permissions, setPermissions] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("permissions")) || {};
    } catch {
      return {};
    }
  });

  // Function to update permissions from sessionStorage
  const updatePermissions = () => {
    try {
      const updatedPermissions = JSON.parse(sessionStorage.getItem("permissions")) || {};
      setPermissions((prev) => {
        // Only update if permissions actually changed
        const prevStr = JSON.stringify(prev);
        const newStr = JSON.stringify(updatedPermissions);
        if (prevStr !== newStr) {
          return updatedPermissions;
        }
        return prev;
      });
    } catch {
      setPermissions({});
    }
  };

  // Update permissions on mount and when location changes
  useEffect(() => {
    updatePermissions();
  }, [location]);

  // Also check permissions immediately on mount
  useEffect(() => {
    updatePermissions();
  }, []);

  // Listen for custom events and storage changes
  useEffect(() => {
    // Update when window gains focus (catches updates after navigation/refresh)
    const handleFocus = () => {
      updatePermissions();
    };

    // Listen for storage events (when sessionStorage is updated from other tabs/windows)
    const handleStorageChange = (e) => {
      if (e.key === "permissions") {
        updatePermissions();
      }
    };

    // Listen for custom permission update events (if dispatched elsewhere in the app)
    const handlePermissionUpdate = () => {
      updatePermissions();
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("permissionsUpdated", handlePermissionUpdate);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("permissionsUpdated", handlePermissionUpdate);
    };
  }, []);

  const get_students = async () => {
    dispatch(startLoader());
    try {
      const res = await getClassReport({ grade: myClass });

      if (res.status == 200) {
        console.log(res.data);
        if (res.data?.topStudents?.length == 0) {
          notifyError("No data available for the selected class");
        }

        setStats(res.data?.stats);
        setSubjectWiseAvg(res.data?.subjectWiseAvg);
        setComparisonWithOtherClasses(res.data?.comparisonWithOtherClasses);
        setTotalStudents(res.data?.total_students);

        setStudents(res.data);
        setFilteredStudents(res.data?.topStudents);
      }
    } catch (error) {
      if (error?.response?.status == 403) {
        notifyError(
          error?.response?.data?.error ||
            "Your session has expired. Please log in again."
        );
        deleteAuthToken();
        navigate("/login");
        return;
      } else if (error?.response?.status == 401) {
        notifyError(
          error?.response?.data?.error ||
            "You are not authorized to access this page."
        );
        return;
      } else {
        notifyError("Error fetching students");
      }
    } finally {
      dispatch(stopLoader());
    }
  };

  const filterStudents = async (e) => {
    // const Myclass = e.target.name == 'myclass' && e.target.value

    // const name = e.target.name == 'name' && e.target.value

    if (!name) {
      if (selectedTog == "top") setFilteredStudents(students?.topStudents);
      if (selectedTog == "bottom")
        setFilteredStudents([...students?.topStudents].reverse());
    }

    if (name) {
      const student_s =
        selectedTog == "top"
          ? students?.topStudents
          : [...students?.topStudents].reverse();
      const stu_s = student_s?.filter((e) =>
        e?.student_name.toLowerCase().startsWith(name.toLowerCase())
      );
      setFilteredStudents(stu_s);

      return;
    }
  };

  const toggleChange = async (e) => {
    if (selectedTog == "top") {
      setFilteredStudents(students?.topStudents);
    } else {
      setFilteredStudents([...students?.topStudents].reverse());
    }
  };

  const handleDownloadExcel = async () => {
    dispatch(startLoader());
    try {
      const response = await downloadExcel({
        grade: myClass,
        subject: subject,
      });
      const file = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${myClass}-class-report-${subject}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error?.response?.status == 403) {
        notifyError(
          error?.response?.data?.error ||
            "Your session has expired. Please log in again."
        );
        deleteAuthToken();
        navigate("/login");
        return;
      } else if (error?.response?.status == 401) {
        notifyError(
          error?.response?.data?.error ||
            "You are not authorized to access this Module."
        );
        return;
      } else {
        notifyError("Error downloading report");
      }
    } finally {
      dispatch(stopLoader());
    }
  };

  useEffect(() => {
    get_students();
  }, [myClass]);

  useEffect(() => {
    filterStudents();
  }, [name, myClass]);

  useEffect(() => {
    toggleChange();
  }, [selectedTog]);

  const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  return (
    <div>
      <div className="ai-fd-main">
        {
          <div className="ai-fd-con">
            <div className="ai-fd-head">
              <div className="ai-fd-head-text">Class Summary</div>
              <div className="ai-fd-right" style={{ alignItems: "end" }}>
                {/* <select className="ai-fd-search">
                <option>Exam</option>
              </select> */}

                <div style={{ width: "100px" }}>
                  <label className="pdf-input-label">Class</label>
                  <SearchableDropdown
                    options={[...numbers]}
                    selected={myClass}
                    setSelected={setMyClass}
                    allowManualEntry={false}
                    color="#FFFFFF"
                  />
                </div>
                <div>
                  <label className="pdf-input-label">Subject</label>
                  <SearchableDropdown
                    options={["All", ...subjectWiseAvg.map((e) => e.subject)]}
                    selected={subject}
                    setSelected={setSubject}
                    placeHolder="Select or type"
                    allowManualEntry={true}
                    color="#FFFFFF"
                  />
                </div>
                <button
                  className="my-btn"
                  style={{ width: "fit-content", display: "flex", alignItems: "center", gap: "4px" }}
                  onClick={handleDownloadExcel}
                  disabled={!permissions?.classReportExcel}
                >
                  Export Analytics
                  {!permissions?.classReportExcel && <LockKeyhole size={16} />}
                </button>
                {/* <select className="ai-fd-search">
                <option>Date</option>
              </select> */}
              </div>
            </div>
            <div className="class-body">
              <div className="class-card-con">
                <FeatureCard
                  title="Total Students"
                  desc={totalStudents}
                  color="#F0FAFF"
                />
                <FeatureCard
                  title="Average Marks"
                  desc={stats?.avg_marks}
                  subText="+1 vs Last Exam"
                  color="#ECFDF5"
                />
                <FeatureCard
                  title="Highest Marks"
                  desc={stats?.max_marks}
                  subText="+1 vs Last Exam"
                  color="#FEF2F2"
                />
                <FeatureCard
                  title="Lowest Marks"
                  desc={stats?.min_marks}
                  subText="+1 vs Last Exam"
                  color="#FEFCE8"
                />
              </div>

              <div className="class-table-con">
                <div className="class-toggle-head-con">
                  <div className="class-toggle-con">
                    <div
                      onClick={() => setselectedTog("top")}
                      className="class-toggle pe-cursor"
                      style={{
                        color: selectedTog == "top" && "#030712",
                        backgroundColor: selectedTog == "top" && "#FFFFFF",
                      }}
                    >
                      Top Students
                    </div>
                    <div
                      onClick={() => setselectedTog("bottom")}
                      className="class-toggle pe-cursor"
                      style={{
                        color: selectedTog == "bottom" && "#030712",
                        backgroundColor: selectedTog == "bottom" && "#FFFFFF",
                      }}
                    >
                      {" "}
                      Bottom Students{" "}
                    </div>
                  </div>

                  <input
                    className="student-list-search"
                    style={{ height: "36px" }}
                    placeholder="Search name"
                    name="name"
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className="ai-fd-table">
                  <div className="ai-fd-table-head">
                    <div className="ai-fd-head-cell" style={{ width: "20%" }}>
                      Student name
                    </div>
                    <div className="ai-fd-head-cell" style={{ width: "20%" }}>
                      Total score
                    </div>
                    <div className="ai-fd-head-cell" style={{ width: "20%" }}>
                      Class
                    </div>
                    <div className="ai-fd-head-cell" style={{ width: "20%" }}>
                      Roll no
                    </div>
                    <div className="ai-fd-head-cell" style={{ width: "20%" }}>
                      Last uploaded on
                    </div>
                  </div>
                  {filteredstudents?.length > 0 &&
                    filteredstudents?.map((e, i) => (
                      <div key={i} className="ai-fd-table-row">
                        <div
                          className="ai-fd-row-cell"
                          style={{ width: "20%" }}
                        >
                          {e.student_name}
                        </div>
                        <div
                          className="ai-fd-row-cell"
                          style={{ width: "20%" }}
                        >
                          {Number(e.total)}{" "}
                        </div>
                        <div
                          className="ai-fd-row-cell"
                          style={{ width: "20%" }}
                        >
                          {formatGrade(e.grade)}
                        </div>
                        <div
                          className="ai-fd-row-cell"
                          style={{ width: "20%" }}
                        >
                          {e.student_rollno}
                        </div>
                        <div
                          className="ai-fd-row-cell"
                          style={{ width: "20%" }}
                        >
                          {formatDateLong(e.last_uploaded)}
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              <div className="class-graph-con">
                <div className="class-graph">
                  <div>Subject wise comparisons for class {myClass}</div>
                  <CustomChart
                    type="bar"
                    xData={[...subjectWiseAvg.map((e) => e.subject)]}
                    yData={subjectWiseAvg.map((e) => e.avg_marks)}
                    color="#01A7DE"
                    horizontal={false}
                    showPlaceholder={false}
                    label="Average Marks"

                    // barGap={0.7}
                  />
                </div>
                <div className="class-graph">
                  <div>Class wise average marks comparisons</div>
                  <CustomChart
                    type="bar"
                    xData={[...comparisonWithOtherClasses.map((e) => e.grade)]}
                    yData={comparisonWithOtherClasses.map((e) => e.avg_marks)}
                    color="#EAB308"
                    horizontal={true}
                    showPlaceholder={true}
                    barGap={0.7}
                    label="Average Marks"
                    yTicks={true}
                  />
                </div>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  );
};

export default ClassUI;
